import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./auth";
import { getWishlist, addToWishlist, removeFromWishlist } from "./api";

// Wishlist for the signed-in account holder, scoped to a venue. Exposes the
// saved records plus a toggle mutation. Cards read `savedIds` for their heart
// state; the wishlist page reads `items`. Only fetches when signed in.
export function useWishlist(venueId: string | undefined) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const enabled = !!user && !!venueId;

  const query = useQuery({
    queryKey: ["wishlist", venueId, user?.id],
    queryFn: () => getWishlist(venueId!),
    enabled,
    staleTime: 30_000,
  });

  const items = query.data?.items ?? [];
  const savedIds = new Set(items.map((i) => i.id));

  const toggle = useMutation({
    mutationFn: async ({ listingId, saved }: { listingId: string; saved: boolean }) => {
      if (!venueId) return;
      if (saved) await removeFromWishlist(venueId, listingId);
      else await addToWishlist(venueId, listingId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wishlist", venueId, user?.id] });
    },
  });

  return {
    enabled,
    items,
    savedIds,
    isLoading: query.isLoading,
    toggle: (listingId: string) => toggle.mutate({ listingId, saved: savedIds.has(listingId) }),
    isToggling: toggle.isPending,
  };
}
